import { CheckOutlined, LoadingOutlined } from "@ant-design/icons";
import { Button } from "antd";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { getEHRTransferStatus } from "../../service/notes.service";
import type { EHRTransferStatus, TransferJob } from "../../types/index.type";
import { useOptionalTransfer } from "./TransferContext";

const TERMINAL_STATUSES = new Set<EHRTransferStatus>(["completed", "failed", "cancelled"]);

interface TransferToEHRButtonProps {
  // The note this button initiates a transfer for. Required so the global
  // TransferContext knows which audio to transfer, and so the button can tell
  // whether the user's active transfer is for THIS note vs. a different one.
  audioId: number;
  className?: string;
  icon?: React.ReactNode;
}

const ACTIVE_STATUSES = ["pending", "logging_in", "awaiting_mfa", "patient_lookup", "template_mapping", "writing_note"];
const AWAITING_SELECTION_STATUSES = new Set(["awaiting_appointment", "awaiting_note_type"]);

const TransferToEHRButton: React.FC<TransferToEHRButtonProps> = ({ audioId, className = "", icon: defaultIcon }) => {
  // useOptionalTransfer (not useTransfer) because this header is reused on
  // `/notesforMobile`, which is routed outside AuthLayout and therefore has
  // no TransferProvider. Throwing there crashed the whole subtree.
  const transfer = useOptionalTransfer();
  // This note's own live job (independent of other notes' transfers, which can
  // now run concurrently). Recomputed from the context's job list each render.
  const thisNoteJob = transfer?.getJobForAudio(audioId) ?? null;

  // Per-note historical status. The global TransferContext only knows about
  // *active* (or just-finished) jobs, so a note transferred yesterday looks
  // like "not_started" to it. We fetch this note's most recent job on mount
  // so a previously-transferred note correctly renders "Transferred".
  const [historicalJob, setHistoricalJob] = useState<TransferJob | null>(null);

  useEffect(() => {
    let cancelled = false;
    getEHRTransferStatus(audioId)
      .then((res) => {
        if (cancelled) return;
        setHistoricalJob(res.data?.job ?? null);
      })
      .catch((err) => console.error("Failed to fetch transfer history for note:", err));
    return () => {
      cancelled = true;
    };
  }, [audioId]);

  // When this note's live job reaches a terminal state, persist it as the
  // historical state so the button stays correct even after the active
  // endpoint stops returning the job. A ref guard makes the per-poll effect
  // idempotent (the live job object is a fresh reference on every poll).
  const lastPersistedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!thisNoteJob || !TERMINAL_STATUSES.has(thisNoteJob.status)) return;
    const key = `${thisNoteJob.id}:${thisNoteJob.status}`;
    if (lastPersistedRef.current === key) return;
    lastPersistedRef.current = key;
    setHistoricalJob(thisNoteJob);
  }, [thisNoteJob]);

  if (!transfer) return null;
  const { isLoading, credentialStatus, startTransfer } = transfer;

  // Effective status priority: live job for this note > historical > idle.
  const effectiveStatus: EHRTransferStatus =
    (thisNoteJob?.status as EHRTransferStatus | undefined) ??
    (historicalJob?.status as EHRTransferStatus | undefined) ??
    "not_started";

  const isInProgress = ACTIVE_STATUSES.includes(effectiveStatus);
  const isAwaitingSelection = AWAITING_SELECTION_STATUSES.has(effectiveStatus);
  const isAwaitingMFA = effectiveStatus === "awaiting_mfa";
  const isCompleted = effectiveStatus === "completed";
  // Cancelled is retryable just like failed.
  const isRetryable = effectiveStatus === "failed" || effectiveStatus === "cancelled";
  const isNoteTypeSelection = effectiveStatus === "awaiting_note_type";
  const hasCredentials = credentialStatus?.has_credential ?? false;
  // NOTE: no cross-note "another transfer running" block — concurrent
  // transfers across different notes are supported.
  const isDisabled = isLoading || isInProgress || isCompleted || isAwaitingSelection || isAwaitingMFA;

  let buttonText = "Transfer to EHR";
  let icon: React.ReactNode = null;

  const isProcessingState = isLoading || isInProgress || isAwaitingSelection || isAwaitingMFA;
  const style = { color: "white" };
  if (isLoading) {
    buttonText = "Checking...";
    icon = <LoadingOutlined style={style} />;
  } else if (isCompleted) {
    buttonText = "Transferred";
    icon = <CheckOutlined style={style} />;
  } else if (isRetryable) {
    buttonText = "Retry Transfer";
  } else if (!hasCredentials) {
    buttonText = "Transfer to EHR";
  } else if (isInProgress) {
    buttonText = "Transferring...";
    icon = <LoadingOutlined style={style} />;
  } else if (isAwaitingMFA) {
    buttonText = "Enter MFA Code...";
    icon = <LoadingOutlined style={style} />;
  } else if (isAwaitingSelection) {
    buttonText = isNoteTypeSelection ? "Select Note Template..." : "Select Appointment...";
    icon = <LoadingOutlined style={style} />;
  } else if (hasCredentials) {
    buttonText = "Transfer to EHR";
  }

  return (
    <Button
      type="primary"
      className={className}
      style={{
        display: "flex",
        alignItems: "center",
        ...((isProcessingState || isCompleted) && {
          backgroundColor: "#6366F1",
          borderColor: "#6366F1",
          color: "white",
          opacity: 1,
        }),
      }}
      onClick={() => startTransfer(audioId)}
      disabled={isDisabled}
      icon={icon ?? defaultIcon}
    >
      {buttonText}
    </Button>
  );
};

export default TransferToEHRButton;
